import React, { useState } from 'react';
import { Box, Autocomplete, TextField, Popover } from '@mui/material';
import { getUser } from '@/lib/common';

// Brand-colour set; a name hashes to one deterministically so an avatar keeps
// the same colour everywhere.
const AVATAR_COLORS = ['#1C71E3', '#7A4FD6', '#16A55C', '#E0863A', '#D6457A', '#0E8A8A'];
const hashColor = (name: string) => {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const initials = (name: string) => {
  const p = (name || '').trim().split(/\s+/);
  return (((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase()) || '?';
};

interface Participant {
  name: string;
  photo: string;
}

interface DisputeMember {
  user_id: string;
  username: string;
  photo_path: string;
}

interface DisputeParticipantsProps {
  sender: Participant;
  receiver: Participant;
  disputeMembers: DisputeMember[];
  availableUsers: any[];
  canManageParticipants?: boolean;
  onAssignOwner: (userId: string) => void;
  onDeleteDisputeMember: (userId: string) => void;
}

type StackItem = {
  name: string;
  photo?: string;
  removable: boolean;
  user_id?: string;
  role?: string;
};

function Avatar({
  item,
  size,
  removable,
  onRemove,
}: {
  item: StackItem;
  size: number;
  removable: boolean;
  onRemove?: () => void;
}) {
  return (
    <Box
      className="cc-avatar"
      sx={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: '50%',
        border: '2px solid #fff',
        background: item.photo ? `center/cover no-repeat url(${item.photo})` : hashColor(item.name),
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.round(size * 0.37),
        fontWeight: 800,
        flex: 'none',
        boxSizing: 'border-box',
        '& .cc-rm': { opacity: 0 },
        '&:hover .cc-rm': { opacity: 1 },
      }}
      title={item.name}>
      {!item.photo && initials(item.name)}
      {removable && onRemove && (
        <Box
          component="button"
          type="button"
          className="cc-rm"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={`Remove ${item.name}`}
          sx={{
            position: 'absolute',
            top: -5,
            right: -5,
            width: 16,
            height: 16,
            borderRadius: '50%',
            border: '1px solid #EBD1D1',
            background: '#FDEBEB',
            color: '#E23D3D',
            fontSize: 11,
            lineHeight: 1,
            cursor: 'pointer',
            transition: 'opacity .12s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0,
          }}>
          ×
        </Box>
      )}
    </Box>
  );
}

const DisputeParticipants: React.FC<DisputeParticipantsProps> = ({
  sender,
  receiver,
  disputeMembers,
  availableUsers,
  canManageParticipants = false,
  onAssignOwner,
  onDeleteDisputeMember,
}) => {
  const user = getUser();
  const canRemove = !!(user?.is_admin || user?.is_sponsor || canManageParticipants);
  const [showAdd, setShowAdd] = useState(false);
  const [listAnchor, setListAnchor] = useState<HTMLElement | null>(null);

  const stack: StackItem[] = [];
  const push = (
    name?: string,
    photo?: string,
    removable = false,
    user_id?: string,
    role?: string,
  ) => {
    if (!name) return;
    if (stack.some((s) => s.name === name)) return;
    stack.push({ name, photo, removable, user_id, role });
  };
  push(sender?.name, sender?.photo, false, undefined, 'Author');
  push(receiver?.name, receiver?.photo, false, undefined, 'Receiver');
  disputeMembers?.forEach((m) =>
    push(m.username, m.photo_path, true, m.user_id, 'Participant'),
  );

  const countLabel = `${stack.length} ${stack.length === 1 ? 'person' : 'people'}`;
  const listOpen = Boolean(listAnchor);

  const openList = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setListAnchor(e.currentTarget);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
      }}>
      <Box
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: '.4px',
          textTransform: 'uppercase',
          color: '#9BA6B7',
          flex: 'none',
        }}>
        Participants
      </Box>

      <Box
        component="button"
        type="button"
        onClick={openList}
        aria-label={`Show ${countLabel}`}
        aria-haspopup="dialog"
        aria-expanded={listOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pl: '8px',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          borderRadius: '8px',
          p: '2px 6px 2px 8px',
          '&:hover': { background: '#F1F5FB' },
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {stack.map((item, i) => (
            <Box
              key={item.name}
              sx={{ ml: i === 0 ? 0 : '-8px', zIndex: stack.length - i, position: 'relative' }}>
              <Avatar item={item} size={30} removable={false} />
            </Box>
          ))}
        </Box>

        <Box
          component="span"
          sx={{
            fontSize: 12.5,
            fontWeight: 800,
            color: '#1C71E3',
            textDecoration: 'underline',
            textUnderlineOffset: '2px',
            flex: 'none',
          }}>
          {countLabel}
        </Box>
      </Box>

      <Popover
        open={listOpen}
        anchorEl={listAnchor}
        onClose={() => setListAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ zIndex: 100000 }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              minWidth: 260,
              maxWidth: 320,
              maxHeight: 360,
              borderRadius: '10px',
              border: '1px solid #E5EAF0',
              boxShadow: '0 10px 28px rgba(16,35,58,.14)',
              overflow: 'hidden',
            },
          },
        }}>
        <Box
          sx={{
            px: 1.75,
            py: 1.25,
            borderBottom: '1px solid #EEF1F6',
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: '.4px',
            textTransform: 'uppercase',
            color: '#9BA6B7',
          }}>
          {countLabel}
        </Box>
        <Box sx={{ py: 0.5, overflowY: 'auto', maxHeight: 300 }}>
          {stack.map((item) => (
            <Box
              key={item.name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.75,
                py: 1,
                '&:hover': { background: '#F7FAFE' },
              }}>
              <Avatar item={item} size={32} removable={false} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box
                  sx={{
                    fontSize: 13.5,
                    fontWeight: 700,
                    color: '#16233A',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                  {item.name}
                </Box>
                {item.role && (
                  <Box sx={{ fontSize: 11.5, fontWeight: 600, color: '#8A94A6' }}>
                    {item.role}
                  </Box>
                )}
              </Box>
              {canRemove && item.removable && item.user_id && (
                <Box
                  component="button"
                  type="button"
                  onClick={() => onDeleteDisputeMember(item.user_id!)}
                  aria-label={`Remove ${item.name}`}
                  sx={{
                    border: '1px solid #EBD1D1',
                    background: '#FDEBEB',
                    color: '#E23D3D',
                    borderRadius: '6px',
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: 'Lato, sans-serif',
                    cursor: 'pointer',
                    px: 1,
                    py: 0.4,
                    flex: 'none',
                    '&:hover': { background: '#FAD9D9' },
                  }}>
                  Remove
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Popover>

      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
        {showAdd ? (
          <Autocomplete
            disablePortal
            open
            size="small"
            sx={{
              width: 240,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                background: '#F7FAFE',
                fontSize: 13,
              },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#C3D0E2' },
            }}
            slotProps={{ popper: { sx: { zIndex: 100000 } } }}
            ListboxProps={{ style: { maxHeight: 220 } }}
            options={availableUsers}
            getOptionLabel={(option: any) => option.username}
            renderInput={(params) => (
              <TextField {...params} autoFocus placeholder="Add participant…" />
            )}
            onBlur={() => setShowAdd(false)}
            onChange={(_event, value: any) => {
              if (value) onAssignOwner(value.id);
              setShowAdd(false);
            }}
          />
        ) : (
          <Box
            component="button"
            type="button"
            onClick={() => setShowAdd(true)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              padding: '6px 12px',
              border: '1px dashed #C3D0E2',
              background: '#F7FAFE',
              color: '#1C71E3',
              borderRadius: '8px',
              fontSize: 12.5,
              fontWeight: 700,
              fontFamily: 'Lato, sans-serif',
              cursor: 'pointer',
              '&:hover': { background: '#EAF2FD' },
            }}>
            + Add participant
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DisputeParticipants;
